import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    IAddParticipantsRequest, ICriterionRequest, ICycleDetail, ICycleRequest, ICycleSummary,
    IReviewDetail, IReviewPartRequest, IReviewSummary, ReviewFilters,
} from '../interfaces/appraisal.interface';

const E = ENDPOINTS.APPRAISAL;
const auth = () => ({ headers: AuthService.getAuthHeader() });
const json = () => ({ headers: { ...AuthService.getAuthHeader(), 'Content-Type': 'application/json' } });

export const AppraisalService = {
    // Cycles (admin)
    listCycles: async (institutionId: string): Promise<ICycleSummary[]> =>
        (await axios.get(E.CYCLES(institutionId), auth())).data,

    getCycle: async (institutionId: string, cycleId: string): Promise<ICycleDetail> =>
        (await axios.get(E.CYCLE(institutionId, cycleId), auth())).data,

    createCycle: async (institutionId: string, request: ICycleRequest): Promise<ICycleDetail> =>
        (await axios.post(E.CYCLES(institutionId), request, json())).data,

    updateCycle: async (institutionId: string, cycleId: string, request: ICycleRequest): Promise<ICycleDetail> =>
        (await axios.put(E.CYCLE(institutionId, cycleId), request, json())).data,

    deleteCycle: async (institutionId: string, cycleId: string): Promise<void> => {
        await axios.delete(E.CYCLE(institutionId, cycleId), auth());
    },

    replaceCriteria: async (institutionId: string, cycleId: string, criteria: ICriterionRequest[]): Promise<ICycleDetail> =>
        (await axios.put(E.CRITERIA(institutionId, cycleId), criteria, json())).data,

    copyCriteria: async (institutionId: string, cycleId: string, sourceCycleId: string): Promise<ICycleDetail> =>
        (await axios.post(E.COPY_CRITERIA(institutionId, cycleId, sourceCycleId), undefined, auth())).data,

    startCycle: async (institutionId: string, cycleId: string): Promise<ICycleDetail> =>
        (await axios.post(E.START(institutionId, cycleId), undefined, auth())).data,

    closeCycle: async (institutionId: string, cycleId: string): Promise<ICycleDetail> =>
        (await axios.post(E.CLOSE(institutionId, cycleId), undefined, auth())).data,

    // Participants (admin)
    addParticipants: async (institutionId: string, cycleId: string, request: IAddParticipantsRequest): Promise<IReviewSummary[]> =>
        (await axios.post(E.PARTICIPANTS(institutionId, cycleId), request, json())).data,

    removeParticipant: async (institutionId: string, cycleId: string, reviewId: string): Promise<void> => {
        await axios.delete(E.PARTICIPANT(institutionId, cycleId, reviewId), auth());
    },

    setReviewer: async (institutionId: string, cycleId: string, reviewId: string, reviewerUserId: string): Promise<IReviewSummary> =>
        (await axios.put(E.PARTICIPANT_REVIEWER(institutionId, cycleId, reviewId), { reviewerUserId }, json())).data,

    cycleReviews: async (institutionId: string, cycleId: string, filters: ReviewFilters = {}): Promise<IReviewSummary[]> => {
        const params: Record<string, string> = {};
        if (filters.status) params.status = filters.status;
        if (filters.departmentId) params.departmentId = filters.departmentId;
        return (await axios.get(E.CYCLE_REVIEWS(institutionId, cycleId), { ...auth(), params })).data;
    },

    // Mine
    myReviews: async (institutionId: string): Promise<IReviewSummary[]> =>
        (await axios.get(E.MY_REVIEWS(institutionId), auth())).data,

    reviewsToWrite: async (institutionId: string): Promise<IReviewSummary[]> =>
        (await axios.get(E.TO_WRITE(institutionId), auth())).data,

    // One review
    getReview: async (institutionId: string, reviewId: string): Promise<IReviewDetail> =>
        (await axios.get(E.REVIEW(institutionId, reviewId), auth())).data,

    saveSelf: async (institutionId: string, reviewId: string, part: IReviewPartRequest): Promise<IReviewDetail> =>
        (await axios.put(E.SELF(institutionId, reviewId), part, json())).data,

    submitSelf: async (institutionId: string, reviewId: string, part: IReviewPartRequest): Promise<IReviewDetail> =>
        (await axios.post(E.SELF_SUBMIT(institutionId, reviewId), part, json())).data,

    saveManager: async (institutionId: string, reviewId: string, part: IReviewPartRequest): Promise<IReviewDetail> =>
        (await axios.put(E.MANAGER(institutionId, reviewId), part, json())).data,

    submitManager: async (institutionId: string, reviewId: string, part: IReviewPartRequest): Promise<IReviewDetail> =>
        (await axios.post(E.MANAGER_SUBMIT(institutionId, reviewId), part, json())).data,

    acknowledge: async (institutionId: string, reviewId: string, comment?: string): Promise<IReviewDetail> =>
        (await axios.post(E.ACKNOWLEDGE(institutionId, reviewId), { comment }, json())).data,
};
